/**
 * Notificările utilizatorului: alerte de securitate, starea canalelor, limite de plan.
 * Fiecare notificare are un link direct către ecranul relevant.
 */
import { Link } from 'react-router-dom';
import { Badge, Button, Card, EmptyState, Icon, InlineAlert, PageHeader, Skeleton, cx } from '../components/ui.js';
import { api } from '../lib/api.js';
import { relativeTime, useResource } from '../lib/hooks.js';
import { errorMessage, useSession } from '../lib/session.js';
import { useToast } from '../components/ui.js';

interface Notification {
  id: string;
  type: string;
  severity: 'info' | 'success' | 'warning' | 'critical';
  title: string;
  body: string | null;
  action_url: string | null;
  read_at: string | null;
  created_at: string;
}

export function NotificationsPage() {
  const session = useSession();
  const toast = useToast();
  const resource = useResource<Notification[]>(() => api.get<Notification[]>('/notifications'), [session.activeAccountId]);

  async function markRead(id: string) {
    try {
      await api.post(`/notifications/${id}/read`);
      await resource.reload();
      await session.refresh();
    } catch (error) {
      toast.error('Nu am putut marca notificarea', errorMessage(error));
    }
  }

  async function markAll() {
    try {
      await api.post('/notifications/read-all');
      toast.success('Toate notificările au fost marcate ca citite');
      await resource.reload();
      await session.refresh();
    } catch (error) {
      toast.error('Nu am putut actualiza notificările', errorMessage(error));
    }
  }

  return (
    <div className="page-enter">
      <PageHeader
        title="Notificări"
        subtitle="Alerte de securitate, starea ecranelor și limitele abonamentului."
        actions={
          resource.data?.some((item) => !item.read_at) ? (
            <Button variant="secondary" onClick={() => void markAll()}>
              Marchează tot ca citit
            </Button>
          ) : null
        }
      />

      {resource.loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-16" />
          ))}
        </div>
      ) : resource.error ? (
        <InlineAlert tone="warning" title="Nu am putut încărca notificările">
          {resource.error}
        </InlineAlert>
      ) : !resource.data?.length ? (
        <EmptyState
          icon={<Icon name="bell" size={28} />}
          title="Nicio notificare"
          description="Aici apar alertele importante: autentificări noi, ecrane offline, limite apropiate."
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {resource.data.map((notification) => (
            <Card
              as="li"
              key={notification.id}
              className={cx('flex items-start gap-4', !notification.read_at && 'border-brand-100 bg-brand-100/20')}
            >
              <span
                className={cx(
                  'mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-sm)]',
                  notification.severity === 'critical'
                    ? 'bg-danger-bg text-danger'
                    : notification.severity === 'warning'
                      ? 'bg-warning-bg text-warning'
                      : notification.severity === 'success'
                        ? 'bg-success-bg text-success'
                        : 'bg-info-bg text-info',
                )}
              >
                <Icon name={notification.severity === 'critical' ? 'shield' : 'bell'} size={17} />
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-body-s font-medium text-ink-950">{notification.title}</span>
                  {!notification.read_at ? <Badge tone="brand">nou</Badge> : null}
                </div>
                {notification.body ? <p className="text-body-s text-ink-600">{notification.body}</p> : null}
                <span className="text-caption text-ink-400">{relativeTime(notification.created_at)}</span>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                {notification.action_url ? (
                  <Link to={notification.action_url} className="text-body-s font-medium text-brand-600 hover:text-brand-700">
                    Deschide
                  </Link>
                ) : null}
                {!notification.read_at ? (
                  <Button variant="ghost" size="sm" onClick={() => void markRead(notification.id)}>
                    Marchează citit
                  </Button>
                ) : null}
              </div>
            </Card>
          ))}
        </ul>
      )}
    </div>
  );
}
